import React, { useEffect, useRef } from 'react';

const BAR_COUNT = 7;
const WAVEFORM_HEIGHT = 44;

const waveformStyle = {
  height: WAVEFORM_HEIGHT,
};

const barStyle = (isLast) => ({
  width: 5,
  height: WAVEFORM_HEIGHT,
  borderRadius: 3,
  backgroundColor: 'currentColor',
  transformOrigin: 'center',
  transform: 'scaleY(0.15)',
  marginRight: isLast ? 0 : 4,
});

/**
 * Live audio-level visualizer for the dictation recording view. Taps the active
 * mic `stream` with a Web Audio AnalyserNode and scales each bar to a frequency
 * band's energy, so the bars actually move with the user's voice.
 *
 * Bars are updated directly via refs in a requestAnimationFrame loop (no React
 * re-renders per frame). When there's no stream the bars sit at their baseline.
 */
export default function Waveform({ stream }) {
  const barRefs = useRef([]);
  const levelsRef = useRef(new Array(BAR_COUNT).fill(0));

  useEffect(() => {
    if (!stream) {
      return undefined;
    }
    const AudioCtx = window.AudioContext;
    const audioContext = new AudioCtx();
    if (audioContext.state === 'suspended') {
      audioContext.resume();
    }
    const source = audioContext.createMediaStreamSource(stream);
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 64;
    analyser.smoothingTimeConstant = 0.7;
    source.connect(analyser);

    const bins = analyser.frequencyBinCount;
    const data = new Uint8Array(bins);
    // Voice energy lives in the lower bins; ignore the very top of the range.
    const usableBins = Math.max(BAR_COUNT, Math.floor(bins * 0.7));
    let raf = null;

    const draw = () => {
      analyser.getByteFrequencyData(data);
      for (let i = 0; i < BAR_COUNT; i++) {
        const start = Math.floor((i / BAR_COUNT) * usableBins);
        const end = Math.floor(((i + 1) / BAR_COUNT) * usableBins);
        let sum = 0;
        for (let j = start; j < end; j++) {
          sum += data[j];
        }
        const avg = sum / Math.max(1, end - start); // 0–255
        const target = Math.min(1, avg / 170);
        // Smooth so the bars glide rather than jitter.
        const smoothed = levelsRef.current[i] * 0.6 + target * 0.4;
        levelsRef.current[i] = smoothed;
        const bar = barRefs.current[i];
        if (bar) {
          bar.style.transform = `scaleY(${0.15 + smoothed * 0.85})`;
        }
      }
      raf = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      if (raf) {
        cancelAnimationFrame(raf);
      }
      source.disconnect();
      analyser.disconnect();
      audioContext.close();
    };
  }, [stream]);

  return (
    <div
      className="d-flex align-items-center text-success"
      style={waveformStyle}
      aria-hidden="true"
    >
      {Array.from({ length: BAR_COUNT }).map((_, i) => (
        <span
          key={i}
          style={barStyle(i === BAR_COUNT - 1)}
          ref={(el) => {
            barRefs.current[i] = el;
          }}
        />
      ))}
    </div>
  );
}
